import { Sparkles, CheckCircle2, FileText, Users, BarChart3, Target, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const Methodology = () => {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="hidden md:block">
                <span className="text-xl font-bold">KAILA</span>
                <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate("/")}>홈</Button>
              <Button variant="ghost" onClick={() => navigate("/about")}>소개</Button>
              <Button variant="default" className="bg-blue-500 hover:bg-blue-600">방법론</Button>
              <Button variant="outline" onClick={() => navigate('/consultation')}>
                상담신청
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-blue-500 text-white">진단 방법론</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              방법론 및 세부 절차
            </h1>
            <p className="text-xl text-muted-foreground">
              국제표준(ISO/IEC 42001, IEEE 7000)과 KAILA 노하우를 결합한 4-Phase 12-Step 접근법
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-12">
            
            {/* 진단 수행 방법론 */}
            <Card className="border-2 border-border">
              <CardHeader>
                <CardTitle className="text-2xl">진단 수행 방법론</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                
                {/* 1. 진단 준비 단계 */}
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    진단 준비 단계
                  </h3>
                  <div className="space-y-3 ml-10">
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <p><strong>진단 목표 및 범위 설정:</strong> 조직과의 협의를 통해 진단의 구체적인 목표, 대상 범위, 평가 기준 등을 명확히 설정합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <p><strong>정보 수집 및 분석:</strong> 조직의 AI 활용 현황, 관련 정책 및 규정, 조직 구조 등에 대한 사전 정보를 수집하고 분석합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <p><strong>진단 도구 및 방법 확정:</strong> 설문 조사, 인터뷰, 워크숍, 문서 검토 등 조직의 특성에 맞는 최적의 진단 도구와 방법을 결정합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <p><strong>진단 일정 및 역할 분담:</strong> 효율적인 진단 수행을 위해 상세한 일정 계획을 수립하고, 조직 담당자와의 역할 분담을 명확히 합니다.</p>
                    </div>
                  </div>
                </div>

                {/* 2. 데이터 수집 단계 */}
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    데이터 수집 단계
                  </h3>
                  <div className="space-y-3 ml-10">
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <p><strong>설문 조사:</strong> 조직 구성원을 대상으로 AI 이해도, 활용 경험, 윤리 인식, 법규 준수 등에 대한 설문 조사를 실시합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <p><strong>심층 인터뷰:</strong> 주요 부서 담당자 또는 AI 관련 업무 담당자와의 개별 또는 그룹 인터뷰를 통해 심층적인 정보를 수집합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <p><strong>문서 검토:</strong> AI 관련 정책, 지침, 계약서, 활용 사례 등 관련 문서를 검토하여 실제 운영 현황을 파악합니다.</p>
                    </div>
                  </div>
                </div>

                {/* 3. 데이터 분석 및 평가 단계 */}
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    데이터 분석 및 평가 단계
                  </h3>
                  <div className="space-y-3 ml-10">
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <p><strong>수집 데이터 정량 및 정성 분석:</strong> 설문 조사 결과는 통계 분석하고, 인터뷰 및 워크숍 내용은 질적 분석을 수행합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <p><strong>핵심 지표 기반 평가:</strong> 사전에 설정된 12가지 핵심 지표를 중심으로 데이터 분석 결과를 평가합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <p><strong>강점 및 약점 도출:</strong> 데이터 분석 및 평가 결과를 바탕으로 조직의 AI 리터러시 수준에 대한 강점과 개선이 필요한 약점을 명확하게 도출합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <p><strong>법률 및 윤리적 위험 분석:</strong> AI 활용 과정에서의 잠재적인 법률 및 윤리적 위반 가능성을 분석하고 평가합니다.</p>
                    </div>
                  </div>
                </div>

                {/* 4. 결과 보고 및 개선 방안 제시 단계 */}
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                    결과 보고 및 개선 방안 제시 단계
                  </h3>
                  <div className="space-y-3 ml-10">
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <p><strong>진단 결과 보고서 작성:</strong> 종합적인 분석 결과, 핵심 평가 내용, 조직의 AI 리터러시 수준, 잠재적 위험 등을 상세하게 담은 보고서를 작성합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <p><strong>개선 방안 워크숍:</strong> 진단 결과를 공유하고, 조직 담당자들과 함께 실질적인 개선 방안을 논의하는 워크숍을 진행합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <p><strong>맞춤형 개선 방안 제시:</strong> 조직의 특성과 진단 결과를 고려하여 AI 교육 프로그램 개발, 내부 지침 수립, 외부 전문가 컨설팅 등 맞춤형 개선 방안을 구체적으로 제시합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <p><strong>결과 발표 및 질의응답:</strong> 최종 진단 결과 및 개선 방안을 조직 경영진 및 관련 부서에 발표하고 질의응답 시간을 갖습니다.</p>
                    </div>
                  </div>
                </div>

                {/* 5. 사후 관리 */}
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</div>
                    사후 관리 (선택 사항)
                  </h3>
                  <div className="space-y-3 ml-10">
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <p><strong>개선 방안 실행 지원:</strong> 제시된 개선 방안의 실행 과정을 지원하고, 필요한 추가적인 자문 및 교육을 제공합니다.</p>
                    </div>
                    <div className="flex gap-2">
                      <CheckCircle2 className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <p><strong>정기적인 재진단:</strong> 조직의 AI 리터러시 수준 변화를 추적하고 지속적인 개선을 위해 정기적인 재진단을 제안합니다.</p>
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>

            {/* 4-Phase 12-Step */}
            <Card className="border-2 border-border">
              <CardHeader>
                <CardTitle className="text-2xl">4-Phase 12-Step 수행 절차</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  
                  {/* Phase 1 */}
                  <div>
                    <h3 className="text-xl font-bold mb-4 bg-blue-500/10 p-3 rounded-lg">Phase 1. Plan</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">Step</TableHead>
                          <TableHead>활동</TableHead>
                          <TableHead>산출물</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-bold">1</TableCell>
                          <TableCell>이해관계자 킥오프 · 범위 정의</TableCell>
                          <TableCell>Project Charter</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">2</TableCell>
                          <TableCell>리스크 가설 수립</TableCell>
                          <TableCell>Hypothesis Matrix</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Phase 2 */}
                  <div>
                    <h3 className="text-xl font-bold mb-4 bg-purple-500/10 p-3 rounded-lg">Phase 2. Assess</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">Step</TableHead>
                          <TableHead>활동</TableHead>
                          <TableHead>산출물</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-bold">3</TableCell>
                          <TableCell>온라인 설문(임직원 50명)</TableCell>
                          <TableCell>Raw Data·Dashboard</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">4</TableCell>
                          <TableCell>인터뷰(FGI, 5명)</TableCell>
                          <TableCell>Transcript·Insight</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">5</TableCell>
                          <TableCell>실사(현장·AI의사결정)</TableCell>
                          <TableCell>Gap Checklist</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Phase 3 */}
                  <div>
                    <h3 className="text-xl font-bold mb-4 bg-green-500/10 p-3 rounded-lg">Phase 3. Analyse</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">Step</TableHead>
                          <TableHead>활동</TableHead>
                          <TableHead>산출물</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-bold">6</TableCell>
                          <TableCell>정량 분석(통계·CBT)</TableCell>
                          <TableCell>리터러시 Scorecard</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">7</TableCell>
                          <TableCell>정성 분석(Thematic)</TableCell>
                          <TableCell>Pain Point Map</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">8</TableCell>
                          <TableCell>규제 매핑</TableCell>
                          <TableCell>Compliance Heatmap</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">9</TableCell>
                          <TableCell>종합 진단·등급화</TableCell>
                          <TableCell>Final Scoring Matrix</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Phase 4 */}
                  <div>
                    <h3 className="text-xl font-bold mb-4 bg-orange-500/10 p-3 rounded-lg">Phase 4. Improve</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">Step</TableHead>
                          <TableHead>활동</TableHead>
                          <TableHead>산출물</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-bold">10</TableCell>
                          <TableCell>개선 로드맵 수립</TableCell>
                          <TableCell>Roadmap (3-year)</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">11</TableCell>
                          <TableCell>교육·가이드 설계</TableCell>
                          <TableCell>Curriculum, Policy Draft</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">12</TableCell>
                          <TableCell>경영진 리포트·워크숍 제안(필요시)</TableCell>
                          <TableCell>Executive Deck</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* 평가 등급체계 */}
            <Card className="border-2 border-border">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Target className="h-6 w-6 text-blue-500" />
                  평가 등급체계
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <p className="text-muted-foreground">
                    본 진단 프레임은 AI 기술의 빠른 발전, 윤리적·법적 규제 강화, 그리고 조직이 직면하는 AI 위험 증가에 대한 배경과 필요성에 기반합니다.
                  </p>

                  {/* 총점 및 평가 기준 */}
                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/5 dark:to-purple-500/5 rounded-lg p-6 border border-blue-500/20">
                    <h3 className="font-bold text-lg mb-4">평가 기준</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-24 font-bold text-blue-600 dark:text-blue-400">총점 100점</div>
                        <div className="flex-1">
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="bg-blue-500/10">이해 30점</Badge>
                            <Badge variant="outline" className="bg-purple-500/10">활용 20점</Badge>
                            <Badge variant="outline" className="bg-green-500/10">법무 25점</Badge>
                            <Badge variant="outline" className="bg-orange-500/10">윤리 25점</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 등급 분류 */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="bg-green-500/5 border-green-500/20">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <h3 className="font-bold text-lg">우수</h3>
                        </div>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">90점 이상</p>
                        <p className="text-sm text-muted-foreground mt-2">종합적 AI 리터러시 수준 우수</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-blue-500/5 border-blue-500/20">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <h3 className="font-bold text-lg">양호</h3>
                        </div>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">75~89점</p>
                        <p className="text-sm text-muted-foreground mt-2">양호한 AI 활용 역량 보유</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-yellow-500/5 border-yellow-500/20">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <h3 className="font-bold text-lg">보통</h3>
                        </div>
                        <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">60~74점</p>
                        <p className="text-sm text-muted-foreground mt-2">기본적 AI 이해도 보유</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-red-500/5 border-red-500/20">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <h3 className="font-bold text-lg">미흡</h3>
                        </div>
                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">60점 미만</p>
                        <p className="text-sm text-muted-foreground mt-2">집중적 역량 강화 필요</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* 가중치 보정 */}
                  <div className="bg-muted/50 rounded-lg p-4 border border-border">
                    <div className="flex items-start gap-2">
                      <BarChart3 className="h-5 w-5 text-purple-500 mt-0.5" />
                      <div>
                        <h4 className="font-bold mb-1">가중치 보정</h4>
                        <p className="text-sm text-muted-foreground">업종·지역별 규제 민감도 반영 (±5점)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 리스크 식별 및 평가 */}
            <Card className="border-2 border-border">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Shield className="h-6 w-6 text-red-500" />
                  리스크 식별 및 평가
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  
                  {/* 리스크 유형 */}
                  <div>
                    <h3 className="font-bold text-lg mb-4">AI 관련 잠재적 리스크 유형</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      
                      <div className="flex gap-3 p-4 bg-red-500/5 rounded-lg border border-red-500/20">
                        <div className="flex-shrink-0 w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
                          <Shield className="h-5 w-5 text-red-500" />
                        </div>
                        <div>
                          <h4 className="font-bold mb-1">기술적 리스크</h4>
                          <p className="text-sm text-muted-foreground">오작동, 오류, 보안 취약점</p>
                        </div>
                      </div>

                      <div className="flex gap-3 p-4 bg-orange-500/5 rounded-lg border border-orange-500/20">
                        <div className="flex-shrink-0 w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                          <Users className="h-5 w-5 text-orange-500" />
                        </div>
                        <div>
                          <h4 className="font-bold mb-1">윤리적 리스크</h4>
                          <p className="text-sm text-muted-foreground">편향성, 차별, 책임 소재 불분명</p>
                        </div>
                      </div>

                      <div className="flex gap-3 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <h4 className="font-bold mb-1">법적 리스크</h4>
                          <p className="text-sm text-muted-foreground">개인정보 침해, 규제 위반</p>
                        </div>
                      </div>

                      <div className="flex gap-3 p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
                        <div className="flex-shrink-0 w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                          <BarChart3 className="h-5 w-5 text-purple-500" />
                        </div>
                        <div>
                          <h4 className="font-bold mb-1">운영 리스크</h4>
                          <p className="text-sm text-muted-foreground">시스템 중단, 데이터 손실</p>
                        </div>
                      </div>

                      <div className="flex gap-3 p-4 bg-green-500/5 rounded-lg border border-green-500/20 md:col-span-2">
                        <div className="flex-shrink-0 w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                          <Users className="h-5 w-5 text-green-500" />
                        </div>
                        <div>
                          <h4 className="font-bold mb-1">사회적 리스크</h4>
                          <p className="text-sm text-muted-foreground">일자리 감소, 사회적 불평등 심화</p>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* 평가 프로세스 */}
                  <div>
                    <h3 className="font-bold text-lg mb-4">리스크 평가 프로세스</h3>
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <p><strong>AI 시스템 및 활용 사례별 리스크 분석:</strong> 조직 내에서 활용되는 특정 AI 시스템 및 활용 사례별로 발생 가능한 리스크를 구체적으로 분석합니다.</p>
                      </div>
                      <div className="flex gap-2">
                        <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <p><strong>리스크 발생 가능성 및 영향도 평가:</strong> 식별된 각 리스크의 발생 가능성과 발생 시 조직에 미치는 잠재적 영향도를 평가하여 리스크의 심각도를 결정합니다.</p>
                      </div>
                      <div className="flex gap-2">
                        <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <p><strong>AI 리터러시 수준 연계 분석:</strong> 조직 구성원의 AI 리터러시 수준이 특정 리스크 발생 가능성 또는 영향도에 미치는 상관관계를 분석합니다.</p>
                      </div>
                    </div>
                  </div>

                  {/* 리스크 매트릭스 */}
                  <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 dark:from-red-500/5 dark:to-orange-500/5 rounded-lg p-6 border border-red-500/20">
                    <h4 className="font-bold mb-3">리스크 매트릭스 예시</h4>
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                      <div className="bg-background/50 p-2 rounded">
                        <div className="font-bold mb-1">발생 가능성</div>
                        <div className="text-xs text-muted-foreground">높음 / 보통 / 낮음</div>
                      </div>
                      <div className="bg-background/50 p-2 rounded">
                        <div className="font-bold mb-1">영향도</div>
                        <div className="text-xs text-muted-foreground">치명적 / 심각 / 경미</div>
                      </div>
                      <div className="bg-background/50 p-2 rounded">
                        <div className="font-bold mb-1">우선순위</div>
                        <div className="text-xs text-muted-foreground">고 / 중 / 저</div>
                      </div>
                    </div>
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <div className="text-center">
              <Button size="lg" className="bg-blue-500 hover:bg-blue-600" onClick={() => navigate("/consultation")}>
                무료 상담 신청하기
              </Button>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold">KAILA</span>
                    <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  기업의 AI 리터러시 수준을 과학적으로 진단하고<br />
                  체계적으로 개선하는 전문 프레임워크
                </p>
              </div>

              <div>
                <h3 className="font-bold mb-4">프레임워크</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    홈
                  </button>
                  <button onClick={() => navigate('/about')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    소개
                  </button>
                  <button onClick={() => navigate('/methodology')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    방법론
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-bold mb-4">문의</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/consultation')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    상담 신청
                  </button>
                  <button onClick={() => navigate('/resources')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    자료 요청
                  </button>
                  <button onClick={() => navigate('/partnership')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    파트너십
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-sm text-muted-foreground">© 2026 KAILA  All rights reserved.</p>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <button onClick={() => navigate('/admin/login')} className="hover:text-foreground transition-colors">
                    관리자 로그인
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>;
};
export default Methodology;